// Note: This hook is used to set the TIN_type options and input mask based on the entity_name value
//       and to reset the TIN_type value when entity_name changes.

import { useEffect } from 'react';

import { useFormStore } from '../../store';

const individualTinTypeOptions = [
  { label: 'SSN', value: 'SSN' },
  { label: 'ITIN', value: 'ITIN' },
];

const entityTinTypeOptions = [{ label: 'EIN', value: 'EIN' }];

const getTinTypeOptions = (entityName) => {
  return entityName === 'Individual'
    ? individualTinTypeOptions
    : entityTinTypeOptions;
};

const resetTinType = (
  formik,
  values,
  entityNameFieldName,
  tinTypeFieldName
) => {
  const currentOptions = getTinTypeOptions(formik.values[entityNameFieldName]);
  const backendValue = values?.TIN_type;

  if (backendValue) {
    const isValidValue = currentOptions.some(
      (option) => option.value === backendValue
    );

    if (isValidValue) {
      formik.setFieldValue(tinTypeFieldName, backendValue);
    } else {
      formik.setFieldValue(tinTypeFieldName, currentOptions[0].value);
    }
  } else {
    formik.setFieldValue(tinTypeFieldName, currentOptions[0].value);
  }
};

const getTinInputMask = (entityName) => {
  if (entityName === 'Individual') {
    return '999-99-9999';
  }
  return '99-9999999';
};

export default function useTinType({
  formik,
  tinTypeFieldName,
  entityNameFieldName,
  formId,
  values,
}) {
  const {
    setTinTypeOptions,
    setTinInputMask,
    removeTinTypeOptions,
    removeTinInputMask,
  } = useFormStore();

  useEffect(() => {
    setTinTypeOptions(
      getTinTypeOptions(formik.values[entityNameFieldName]),
      formId
    );
    setTinInputMask(
      getTinInputMask(formik.values[entityNameFieldName]),
      formId
    );
    resetTinType(formik, values, entityNameFieldName, tinTypeFieldName); // Reset TIN_type when entity_name changes
    formik.setFieldTouched(tinTypeFieldName, false); // Reset TIN_type touch state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values[entityNameFieldName], formId]);

  // Set entity_name field to Individual when TIN_type is SSN or ITIN and to Entity when TIN_type is EIN
  useEffect(() => {
    // If entity_name is already set, don't change it
    if (formik.values[entityNameFieldName]) {
      return;
    }

    if (
      formik.values[tinTypeFieldName] === 'SSN' ||
      formik.values[tinTypeFieldName] === 'ITIN'
    ) {
      formik.setFieldValue(entityNameFieldName, 'Individual');
    } else if (formik.values[tinTypeFieldName] === 'EIN') {
      formik.setFieldValue(entityNameFieldName, 'Entity');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    formik.values[tinTypeFieldName],
    entityNameFieldName,
    values,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    formik.values[entityNameFieldName],
  ]);

  // Cleanup
  useEffect(() => {
    return () => {
      removeTinTypeOptions(formId);
      removeTinInputMask(formId);
    };
  }, [formId, removeTinInputMask, removeTinTypeOptions]);
}
